"use client";

import { motion, AnimatePresence } from "motion/react";
import {
  X,
  User,
  Mail,
  Phone,
  Calendar,
  Users,
  MessageSquare,
  Package,
} from "lucide-react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useRouter } from "next/navigation";
import { useAppDispatch, useAppSelector } from "@/_hooks/hooks";
import {
  insertWebsiteInquiry,
  getAllActiveHotels,
} from "@/services/hotel.services";
import { getAllActiveTours } from "@/services/package.services";
import { ErrorToast } from "./Toast";
import { generateValueLabelRange } from "@/_helper/helper";
import { useEffect } from "react";

interface InquiryModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  tourURL?: string;
  hotelID?: number | null;
  defaultInquiryType?: string;
  defaultPackageName?: string;
}

export default function InquiryModal({
  isOpen,
  onClose,
  title = "Quick Inquiry",
  subtitle = "Fill out the form below and our team will get back to you shortly.",
  tourURL = "",
  hotelID = null,
  defaultInquiryType = "",
  defaultPackageName = "",
}: InquiryModalProps) {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const loading = useAppSelector((state) => state.hotel.loading);
  const HotelList = useAppSelector((state) => state.hotel.HotelList);
  const PackageList = useAppSelector((state) => state.package.PackageList);

  useEffect(() => {
    if (isOpen) {
      if (HotelList.length === 0) {
        dispatch(getAllActiveHotels());
      }
      if (PackageList.length === 0) {
        dispatch(getAllActiveTours());
      }
    }
  }, [isOpen, dispatch, HotelList.length, PackageList.length]);

  const AdultOptions = generateValueLabelRange(1, 10, "string");
  const ChildOptions = generateValueLabelRange(0, 10, "string");

  const formik = useFormik({
    initialValues: {
      FirstName: "",
      EmailID: "",
      MobileNo: "",
      CheckIn: "",
      Adults: "1",
      Child: "0",
      Remarks: "",
      InquiryType: defaultInquiryType || "",
      PackageName: defaultPackageName || "",
    },
    validationSchema: Yup.object({
      FirstName: Yup.string().trim().required("Full Name is required"),
      InquiryType: Yup.string().required("Inquiry Type is required"),
      PackageName: Yup.string().required("Package/Hotel Name is required"),
      MobileNo: Yup.string()
        .required("Mobile number is required")
        .matches(
          /^(?!(\d)\1{9})[6-9]\d{9}$/,
          "Please enter a valid 10-digit mobile number",
        )
        .test(
          "no-sequential-spam",
          "Sequential patterns are not allowed",
          (val) => val !== "1234567890" && val !== "0987654321",
        ),
      EmailID: Yup.string()
        .required("Email is required")
        .test("is-email-required", "Email is not valid", function (value) {
          if (!value) {
            return true;
          }
          const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
          return emailRegex.test(value);
        }),
      CheckIn: Yup.date()
        .required("Expected travel date is required")
        .min(
          new Date(new Date().setHours(0, 0, 0, 0)),
          "Date cannot be in the past",
        ),
      Adults: Yup.string().required("Adults is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      const payload = {
        FirstName: values.FirstName,
        LastName: "",
        Email: values.EmailID,
        MobileNo: values.MobileNo,
        CheckInDate: values.CheckIn,
        CheckOut: null,
        Adults: +values.Adults,
        Child: +values.Child,
        HotelID: hotelID,
        TourURL: tourURL,
        InquiryType: values.InquiryType,
        PackageName: values.PackageName,
        WebsiteURL: typeof window !== "undefined" ? window.location.href : "",
      };
      const response = await dispatch(insertWebsiteInquiry(payload));
      if (response.meta.requestStatus === "fulfilled") {
        resetForm();
        onClose();
        router.push("/thankyou/");
      } else {
        ErrorToast("Error sending Inquiry!");
      }
    },
  });

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden overflow-y-auto max-h-[90vh]"
          >
            <div className="p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {title}
                  </h3>
                  <p className="text-sm text-gray-500">{subtitle}</p>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
                >
                  <X className="w-6 h-6 text-gray-400" />
                </button>
              </div>

              <form onSubmit={formik.handleSubmit} className="space-y-4">
                <div className="space-y-4">
                  {title !== "Inquiry for Package" && (
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                          <Users className="w-4 h-4 text-orange-500" />
                          Inquiry Type
                        </label>
                        <select
                          {...formik.getFieldProps("InquiryType")}
                          onChange={(e) => {
                            formik.setFieldValue("InquiryType", e.target.value);
                            formik.setFieldValue("PackageName", "");
                          }}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition-all text-sm font-medium"
                        >
                          <option value="">Select Inquiry Type</option>
                          <option value="Hotels">Hotels</option>
                          {/* <option value="SOU Packages">SOU Packages</option> */}
                          {/* Gujarat Packages hidden from website; only kept for the Gujarat package detail page */}
                          {defaultInquiryType === "Gujarat Packages" && (
                            <option value="Gujarat Packages">
                              Gujarat Packages
                            </option>
                          )}
                        </select>
                        {formik.touched.InquiryType &&
                          formik.errors.InquiryType && (
                            <div className="text-red-500 text-xs mt-1 font-medium">
                              {formik.errors.InquiryType as string}
                            </div>
                          )}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                          <Package className="w-4 h-4 text-orange-500" />
                          Package/Hotel Name
                        </label>
                        <select
                          {...formik.getFieldProps("PackageName")}
                          disabled={!formik.values.InquiryType}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition-all text-sm font-medium disabled:bg-gray-50"
                        >
                          <option value="">Select Option</option>
                          {formik.values.InquiryType === "Hotels" &&
                            HotelList?.map((hotel: any) => (
                              <option key={hotel?.ID} value={hotel?.HotelName}>
                                {hotel?.HotelName}
                              </option>
                            ))}
                          {/* {formik.values.InquiryType === "SOU Packages" &&
                            PackageList?.filter(
                              (p: any) => p?.PackageType !== "GUJARAT",
                            ).map((pkg: any) => (
                              <option key={pkg?.ID} value={pkg?.TourName}>
                                {pkg?.TourName}
                              </option>
                            ))} */}
                          {formik.values.InquiryType === "Gujarat Packages" &&
                            PackageList?.map((pkg: any) => (
                              <option key={pkg?.ID} value={pkg?.TourName}>
                                {pkg?.TourName}
                              </option>
                            ))}
                        </select>
                        {formik.touched.PackageName &&
                          formik.errors.PackageName && (
                            <div className="text-red-500 text-xs mt-1 font-medium">
                              {formik.errors.PackageName as string}
                            </div>
                          )}
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <User className="w-4 h-4 text-orange-500" />
                      Full Name
                    </label>
                    <input
                      type="text"
                      placeholder="Enter Your Full Name*"
                      {...formik.getFieldProps("FirstName")}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition-all text-sm font-medium"
                    />
                    {formik.touched.FirstName && formik.errors.FirstName && (
                      <div className="text-red-500 text-xs mt-1 font-medium">
                        {formik.errors.FirstName as string}
                      </div>
                    )}
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                        <Mail className="w-4 h-4 text-orange-500" />
                        Email ID
                      </label>
                      <input
                        type="email"
                        placeholder="Your Email*"
                        {...formik.getFieldProps("EmailID")}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition-all text-sm font-medium"
                      />
                      {formik.touched.EmailID && formik.errors.EmailID && (
                        <div className="text-red-500 text-xs mt-1 font-medium">
                          {formik.errors.EmailID as string}
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                        <Phone className="w-4 h-4 text-orange-500" />
                        Mobile No
                      </label>
                      <input
                        type="tel"
                        placeholder="Mobile No.*"
                        {...formik.getFieldProps("MobileNo")}
                        onChange={(e) => {
                          const val = e.target.value
                            .replace(/\D/g, "")
                            .slice(0, 10);
                          formik.setFieldValue("MobileNo", val);
                        }}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition-all text-sm font-medium"
                      />
                      {formik.touched.MobileNo && formik.errors.MobileNo && (
                        <div className="text-red-500 text-xs mt-1 font-medium">
                          {formik.errors.MobileNo as string}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-orange-500" />
                        Travel Date
                      </label>
                      <input
                        type="date"
                        {...formik.getFieldProps("CheckIn")}
                        onClick={(e) => (e.currentTarget as any).showPicker?.()}
                        min={new Date().toISOString().split("T")[0]}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition-all text-sm font-medium cursor-pointer"
                      />
                      {formik.touched.CheckIn && formik.errors.CheckIn && (
                        <div className="text-red-500 text-xs mt-1 font-medium">
                          {formik.errors.CheckIn as string}
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                          <Users className="w-4 h-4 text-orange-500" />
                          Adults
                        </label>
                        <select
                          {...formik.getFieldProps("Adults")}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition-all text-sm font-medium"
                        >
                          {AdultOptions.map((item) => (
                            <option key={item.value} value={item.value}>
                              {item.label}{" "}
                              {+item.value > 1 ? "Adults" : "Adult"}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                          <Users className="w-4 h-4 text-orange-500" />
                          Child
                        </label>
                        <select
                          {...formik.getFieldProps("Child")}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition-all text-sm font-medium"
                        >
                          {ChildOptions.map((item) => (
                            <option key={item.value} value={item.value}>
                              {item.label} {+item.value > 0 && "Child"}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-orange-500" />
                      Message (Optional)
                    </label>
                    <textarea
                      placeholder="Any special requests or queries?"
                      rows={3}
                      {...formik.getFieldProps("Remarks")}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition-all text-sm font-medium resize-none"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={formik.isSubmitting || loading}
                  className="w-full py-4 bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 text-white font-bold text-lg rounded-xl hover:shadow-2xl hover:shadow-orange-500/50 transition-all duration-300 hover:scale-[1.02] flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer"
                >
                  {formik.isSubmitting || loading
                    ? "Sending..."
                    : "Send Inquiry"}
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
